import { sharedSchema } from "@shared-database/pgSchema";
import { File } from "@shared-database/models/file";
import { KnowledgeEntity } from "@shared-database/models/rag/knowledge-entity";
import { KnowledgeGraph } from "@shared-database/models/rag/knowledge-graph";
import {
  leoID,
  leoNotNullForeignID,
  timestampColumns,
} from "@shared-database/utils";
import { sql, type InferInsertModel, type InferSelectModel } from "drizzle-orm";
import { index, real, text, uniqueIndex, vector } from "drizzle-orm/pg-core";

import { EMBEDDING_DIMENSIONS } from "@shared/utils/rag/constants/extraction";

/**
 * Knowledge-graph-scoped relations between entities. The same (source, target,
 * relationType) can have multiple rows across different source files -- this
 * preserves per-file provenance for privacy filtering. Edges are collapsed
 * with summed weights at community-detection or visualization time.
 */
export const KnowledgeRelation = sharedSchema.table(
  "KnowledgeRelation",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),
    knowledgeGraphId: leoNotNullForeignID("knowledgeGraphId").references(
      () => KnowledgeGraph.id,
      { onDelete: "cascade" },
    ),
    relationType: text("relationType").notNull(),
    description: text("description").notNull(),
    weight: real("weight").notNull().default(1),

    /**
     * Every `DocumentChunk.id` within `sourceFileId` that evidenced this
     * edge (parity with GraphRAG's `text_unit_ids[]`). Multiple chunks can
     * assert the same edge; we keep them all so the edge-evidence popover
     * can cite verbatim passages, and so `weight * evidenceCount` can
     * reward edges that show up across the document. Plain `text[]` (no
     * FK) because chunks can churn during reindexing and we'd rather let
     * the array carry stale ids than reject the whole relation row.
     */
    sourceChunkIds: text("sourceChunkIds")
      .array()
      .notNull()
      .default(sql`ARRAY[]::text[]`),

    /**
     * Embedding over `${sourceName} ${relationType} ${targetName}\n${description}`.
     * Enables "relation search" so conceptual queries like "who owns the
     * billing service" can resolve via relation similarity instead of
     * relying solely on entity or chunk matches (parity with LightRAG's
     * dual-level retrieval).
     *
     * NOTE: an HNSW cosine index on this column
     * (`KnowledgeRelation_embedding_hnsw_idx`) exists in the hand-written
     * migration; drizzle-kit has no `hnsw` builder, so we deliberately
     * don't declare it here.
     */
    embedding: vector("embedding", { dimensions: EMBEDDING_DIMENSIONS }),

    // References
    sourceEntityId: leoNotNullForeignID("sourceEntityId").references(
      () => KnowledgeEntity.id,
      { onDelete: "cascade" },
    ),
    targetEntityId: leoNotNullForeignID("targetEntityId").references(
      () => KnowledgeEntity.id,
      { onDelete: "cascade" },
    ),
    sourceFileId: leoNotNullForeignID("sourceFileId").references(
      () => File.id,
      { onDelete: "cascade" },
    ),
  },
  (table) => [
    index("KnowledgeRelation_knowledgeGraphIdx").on(table.knowledgeGraphId),
    index("KnowledgeRelation_sourceEntityIdx").on(table.sourceEntityId),
    index("KnowledgeRelation_targetEntityIdx").on(table.targetEntityId),
    index("KnowledgeRelation_sourceFileIdx").on(table.sourceFileId),
    index("KnowledgeRelation_knowledgeGraphFileIdx").on(
      table.knowledgeGraphId,
      table.sourceFileId,
    ),
    uniqueIndex("KnowledgeRelation_perFileEdgeUniqueIdx").on(
      table.sourceEntityId,
      table.targetEntityId,
      table.relationType,
      table.sourceFileId,
    ),
  ],
);

// Types
export type KnowledgeRelationType = InferSelectModel<typeof KnowledgeRelation>;
export type KnowledgeRelationInsertType = InferInsertModel<
  typeof KnowledgeRelation
>;
